import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Search, Download, ChevronLeft, ChevronRight, Loader2, CalendarCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const todayStr = () => {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

const Stat = ({ label, value, testid }) => (
  <div className="bg-white rounded-lg border border-[#E5DFD0] p-3" data-testid={testid}>
    <p className="text-xs text-[#5A5F6E]">{label}</p>
    <p className="text-xl font-bold text-[#1A1F2E] mt-0.5">{(value ?? 0).toLocaleString()}</p>
  </div>
);

const statusBadge = (s) => {
  if (s === 'PACKED') return 'bg-green-100 text-green-800';
  if (s === 'PARTIALLY_PACKED') return 'bg-yellow-100 text-yellow-800';
  return 'bg-blue-100 text-blue-800';
};

const CompletedByDateTab = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ slips: 0, total_picked: 0, total_packed: 0, total_remaining: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const debounce = useRef(null);

  const buildParams = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    if (search.trim()) p.set('search', search.trim());
    if (dateFrom) p.set('date_from', dateFrom);
    if (dateTo) p.set('date_to', dateTo);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p;
  }, [search, dateFrom, dateTo]);

  const load = useCallback(async (goPage = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const p = buildParams({ page: goPage, page_size: pageSize });
      const res = await fetch(`${API}/api/wms/packing/completed-by-date?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setSummary(data.summary || { slips: 0, total_picked: 0, total_packed: 0, total_remaining: 0 });
      setPage(data.page || goPage);
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [buildParams, pageSize]);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(1), 350);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [search, dateFrom, dateTo, load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('session_token');
      const p = buildParams({ format: 'csv' });
      const res = await fetch(`${API}/api/wms/packing/completed-by-date?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `completed_by_date_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="completed-by-date-tab">
      {/* Filters */}
      <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search pick slip, DC, customer, agent or transport (all pages)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white border-[#E5DFD0]"
              data-testid="cbd-search"
            />
          </div>
          <div>
            <label className="block text-xs text-[#5A5F6E] mb-1">From (completed)</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="bg-white border-[#E5DFD0]" data-testid="cbd-date-from" />
          </div>
          <div>
            <label className="block text-xs text-[#5A5F6E] mb-1">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="bg-white border-[#E5DFD0]" data-testid="cbd-date-to" />
          </div>
          <Button variant="outline" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="border-[#E5DFD0] mb-0.5" data-testid="cbd-clear-dates">
            All dates
          </Button>
          <Button onClick={exportCsv} disabled={exporting}
            className="bg-[#2D5F4F] hover:bg-[#244C40] text-white ml-auto" data-testid="cbd-export-csv">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Slips" value={summary.slips} testid="cbd-summary-slips" />
        <Stat label="Picked" value={summary.total_picked} testid="cbd-summary-picked" />
        <Stat label="Packed" value={summary.total_packed} testid="cbd-summary-packed" />
        <Stat label="Remaining" value={summary.total_remaining} testid="cbd-summary-remaining" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cbd-table">
            <thead className="bg-[#F8F4EE] text-[#5A5F6E]">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Completed Date</th>
                <th className="text-left px-4 py-2 font-medium">Pick Slip No</th>
                <th className="text-left px-4 py-2 font-medium">DC No</th>
                <th className="text-left px-4 py-2 font-medium">Customer</th>
                <th className="text-left px-4 py-2 font-medium">Transport</th>
                <th className="text-right px-4 py-2 font-medium">Picked</th>
                <th className="text-right px-4 py-2 font-medium">Packed</th>
                <th className="text-right px-4 py-2 font-medium">Remaining</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0EADD]">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[#5A5F6E]">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#2D5F4F]" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[#5A5F6E]" data-testid="cbd-empty">
                  <CalendarCheck className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                  No completed pick slips for these filters.
                </td></tr>
              ) : rows.map((r) => (
                <tr key={r.pick_slip_number} className="hover:bg-[#FFFBF5]" data-testid={`cbd-row-${r.pick_slip_number}`}>
                  <td className="px-4 py-2 text-[#1A1F2E]">{r.completed_date || '—'}</td>
                  <td className="px-4 py-2 font-semibold text-[#1A1F2E]">
                    {r.pick_slip_number}
                    {r.is_seasonal && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-lime-100 text-lime-800 border border-lime-300">
                        Seasonal
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-blue-600">{r.dc_number || '—'}</td>
                  <td className="px-4 py-2">{r.customer || '—'}</td>
                  <td className="px-4 py-2">{r.transport_name || '—'}</td>
                  <td className="px-4 py-2 text-right">{r.total_picked}</td>
                  <td className="px-4 py-2 text-right">{r.total_packed}</td>
                  <td className="px-4 py-2 text-right font-semibold text-amber-700" data-testid={`cbd-remaining-${r.pick_slip_number}`}>{r.remaining_to_pack}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(r.packing_status)}`}>
                      {r.packing_status?.replace(/_/g, ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5DFD0] bg-[#FBF8F2]">
          <span className="text-sm text-[#5A5F6E]" data-testid="cbd-page-info">
            {total === 0 ? '0 rows' : `Page ${page} of ${totalPages} · ${total.toLocaleString()} slips`}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={loading || page <= 1}
              onClick={() => load(page - 1)} data-testid="cbd-prev">
              <ChevronLeft className="w-4 h-4" /> Prev
            </Button>
            <Button variant="outline" size="sm" disabled={loading || page >= totalPages}
              onClick={() => load(page + 1)} data-testid="cbd-next">
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletedByDateTab;
